<template>
  <div>
    <div class="simple-service__section">
      <div v-if="!hideTitle" class="simple-service__title">
        {{$t('simpleService.appointment.title')}}
      </div>
      <div v-if="!hideTitle && $t('simpleService.appointment.description')" class="simple-service__description">
        {{$t('simpleService.appointment.description')}}
      </div>

      <div class="simple-service__subtitle">{{$t('simpleService.appointment.selectDate')}} *</div>

      <div v-if="!loading">
        <div v-if="dates.length < 9" class="date__container">
          <div v-for="(date, index) in dates"
              :key="index"
              @click="selectDate(date)"
              class="date__item"
              :class="{'date__item--active': form.selectedDate === date, 'error-border': !validationRules.form.selectedDate.required && validationRules.form.selectedDate.$error}">
            {{date | moment('dddd, LL')}}
          </div>
        </div>
        <div v-else>
          <datepicker-container
            @selectDate="selectCalendarDate"
            :selectedDate="displaySelectedDate()"
            :day-cell-content="cellContent"
            :available-dates="calendarTimeslots"
          />

          <div>
            <div class="simple-service__subtitle">
              {{$t('simpleService.appointment.appointmentLegend')}}
            </div>

            <div class="appointment__description__container">
              <div class="appointment__description__label">
                <div class="appointment__description__label--selected-day"></div>
                <div>{{$t('simpleService.appointment.selectedLabel')}}</div>
              </div>

              <div class="appointment__description__label">
                <div class="appointment__description__label--available"></div>
                <div>{{$t('simpleService.appointment.availableLabel')}}</div>
              </div>

              <div class="appointment__description__label">
                <div class="appointment__description__label--unavailable"></div>
                <div>{{$t('simpleService.appointment.unavailableLabel')}}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!validationRules.form.selectedDate.required && validationRules.form.selectedDate.$error"
           class="error-message">
        {{$t('simpleService.errors.requiredField')}}
      </div>

      <div v-if="loading">
        <spinner size="18" class="spinner spinner--select" />
      </div>

      <div v-if="!loading && !dates.length" class="error-message">
        {{$t('simpleService.appointment.errors.emptySlots')}}
      </div>
    </div>

    <div v-if="form.selectedDate" class="simple-service__section">
      <div class="simple-service__subtitle">{{$t('simpleService.appointment.selectTime')}} *</div>

      <div v-if="timeSlotFilterAvailable" class="date__timeFilter--container">
        <div v-for="filter in timeFilters"
             :key="filter" class="date__timeFilter"
             :class="{'date__item--active': filter === timeFilter}"
             @click="selectTimeFilter(filter)">
          {{ $t(`simpleService.appointment.timeFilter.${filter}`) }}
        </div>
      </div>

      <div class="date__timeslot--container" :key="timeFilter">
        <div v-for="(slot, index) in filteredTimeSlots(form.selectedDate)"
             :key="index"
             @click="selectTime(slot)"
             :class="{'main__disabled': !form.selectedDate, 'date__item--active': form.selectedTimeSlot && form.selectedTimeSlot.id === slot.id,
           'error-border': !validationRules.form.selectedTimeSlot.required && validationRules.form.selectedTimeSlot.$error}"
             class="date__timeslot">
          {{slot.startsAt | timeFilter}}
        </div>
        <div v-if="!filteredTimeSlots(form.selectedDate).length">
          {{$t('simpleService.appointment.empty')}}
        </div>
      </div>

      <div class="form__input--container">
        <div v-if="!validationRules.form.selectedTimeSlot.required && validationRules.form.selectedTimeSlot.$error" class="error-message">
          {{$t('simpleService.errors.requiredField')}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'vue-simple-spinner';
import moment from 'moment';
import DatepickerContainer from '@/components/Datepicker.vue';

export default {
  name: 'AppointmentSelection',
  data: function () {
    return {
      timeFilters: ['morning', 'afternoon'],
      loading: false,
      requestError: false,
      timeFilter: 'morning',
      calendarTimeslots: []
    }
  },
  props: {
    form: Object,
    validationRules: Object,
    hideTitle: {
      default: false
    }
  },
  methods: {
    displaySelectedDate() {
      return this.form.selectedDate ? moment(this.form.selectedDate, 'YYYY-MM-DD').toDate() : null;
    },
    cellContent(day) {
      let date = moment(day.timestamp).format('YYYY-MM-DD');
      const getEl = (date, activeColor = 'unavailable') => {
        const isAvailable = activeColor !== 'unavailable';

        return `
          <div class="datepicker__dot--container datepicker__dot__available--${isAvailable}" data-js="campaign-datepicker__dot--available-${isAvailable}">
              <div class="datepicker__dot--number">${day.date}</div>
              <div>
                  <div class="datepicker__dot datepicker__dot--${activeColor}"></div>
              </div>
          </div>`;
      }

      return this.timeSlots[date] ? getEl(day, 'available') : getEl(day);
    },
    selectCalendarDate(r) {
      this.selectDate(moment(r).format('YYYY-MM-DD'));
    },
    selectTimeFilter (filter) {
      this.timeFilter = filter;
    },
    filteredTimeSlots (selectedDate) {
      if (!this.timeSlotFilterAvailable) {
        return this.timeSlots[selectedDate] || [];
      }

      return this.timeSlots[selectedDate]?.filter(time => {
        if (this.timeFilter === 'morning') {
          return parseInt(moment(time.startsAt).format('HH')) < 12;
        } else if (this.timeFilter === 'afternoon') {
          return parseInt((moment(time.startsAt).format('HH'))) >= 12;
        } else {
          return true;
        }
      }) || [];
    },
    selectTime (time) {
      if (this.form.selectedTimeSlot && this.form.selectedTimeSlot.id === time.id) {
        this.form.selectedTimeSlot = null;
      } else {
        this.form.selectedTimeSlot = time;
      }
    },
    selectDate (date) {
      this.form.selectedTimeSlot = null;

      if (this.form.selectedDate === date) {
        this.form.selectedDate = null;
      } else {
        this.form.selectedDate = date;
      }
    },
    setAvailableDates() {
      this.calendarTimeslots = this.timeSlots.map((timeslot, date) => {
        return {
          date: date,
          start: moment(date, 'YYYY-MM-DD').startOf('day').toDate(),
          end: moment(date, 'YYYY-MM-DD').endOf('day').toDate()
        }
      });
    }
  },
  created() {
    this.setAvailableDates();
  },
  computed: {
    ...mapGetters('capacity', ['timeSlots', 'dates']),
    timeSlotFilterAvailable() {
      if (!this.timeSlots[this.form.selectedDate] || this.timeSlots[this.form.selectedDate].length < 11) {
        return false;
      }

      let morning = false;
      let afternoon = false;
      let mourningCount = 0;
      let afternoonCount = 0;

      for (const timeslot of this.timeSlots[this.form.selectedDate]) {
        if (morning && afternoon && mourningCount >= 4 && afternoonCount >= 4) return true;

        if (parseInt(moment(timeslot.startsAt).format('HH')) < 12) {
          mourningCount++;
          morning = true;
        } else {
          afternoonCount++;
          afternoon = true;
        }
      }

      return false;
    }
  },
  components: {
    Spinner,
    DatepickerContainer
  }
}
</script>
